import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import marketplacesettingsService from '../services/marketplacesettingsService';
import './ConnectorSettingForm.css';

function MarketplaceSettingForm({ mode = 'create' }) {
  const { id, marketplaceid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    MARKETPLACEID: marketplaceid || '',
    SETTINGNAME: '',
    type: '',
    DATATYPE: '',
    AVAILABLEVALUES: '',
    required: 'N',
    is_inheritable: 'Y',
    must_be_overridden: 'N',
    SUB_GROUP: '',
    SETTINGVALUE: ''
  });

  useEffect(() => {
    const initializeForm = async () => {
      if (marketplaceid) {
        setFormData(prev => ({ ...prev, MARKETPLACEID: marketplaceid }));
      }

      if (mode !== 'create' && id) {
        await fetchSetting();
      }
    };

    initializeForm();
  }, [id, mode, marketplaceid]);

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const response = await marketplacesettingsService.getSettingById(id);
      if (response.setting) {
        setFormData(response.setting);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch setting');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.SETTINGNAME) {
      setError('Setting Name is required');
      return;
    }

    try {
      setLoading(true);
      if (mode === 'create') {
        await marketplacesettingsService.createSetting(formData);
      } else if (mode === 'edit') {
        await marketplacesettingsService.updateSetting(id, formData);
      }
      navigate(`/admin/marketplace/${marketplaceid}/edit?tab=marketplacesettings`);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${mode} setting`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this setting?')) {
      return;
    }

    try {
      setLoading(true);
      await marketplacesettingsService.deleteSetting(id);
      navigate(`/admin/marketplace/${marketplaceid}/edit?tab=marketplacesettings`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete setting');
      setLoading(false);
    }
  };

  if (loading && mode !== 'create') {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h2>
          {mode === 'create' && 'Add Marketplace Setting'}
          {mode === 'edit' && 'Edit Marketplace Setting'}
          {mode === 'view' && 'Marketplace Setting Details'}
        </h2>
        <button
          type="button"
          onClick={() => navigate(`/admin/marketplace/${marketplaceid}/edit?tab=marketplacesettings`)}
          className="btn-secondary"
        >
          Back to Marketplace
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Setting Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="SETTINGNAME">Setting Name *</label>
              <input
                type="text"
                id="SETTINGNAME"
                name="SETTINGNAME"
                value={formData.SETTINGNAME || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                placeholder="e.g., API_KEY, ENDPOINT_URL, TIMEOUT"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="MARKETPLACEID">Marketplace ID</label>
              <input
                type="number"
                id="MARKETPLACEID"
                name="MARKETPLACEID"
                value={formData.MARKETPLACEID || ''}
                onChange={handleChange}
                disabled={true}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Type</label>
              <select
                id="type"
                name="type"
                value={formData.type || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
              >
                <option value="">-- Select Type --</option>
                <option value="AUTH">AUTH</option>
                <option value="CONFIG">CONFIG</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="DATATYPE">Data Type</label>
              <select
                id="DATATYPE"
                name="DATATYPE"
                value={formData.DATATYPE || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
              >
                <option value="">-- Select Data Type --</option>
                <option value="TEXT">TEXT</option>
                <option value="PASSWORD">PASSWORD</option>
                <option value="NUMBER">NUMBER</option>
                <option value="SELECT">SELECT</option>
              </select>
            </div>
          </div>

          {formData.DATATYPE === 'SELECT' && (
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="AVAILABLEVALUES">Available Values</label>
                <input
                  type="text"
                  id="AVAILABLEVALUES"
                  name="AVAILABLEVALUES"
                  value={formData.AVAILABLEVALUES || ''}
                  onChange={handleChange}
                  disabled={mode === 'view'}
                  placeholder="Comma-separated label:value pairs, e.g. Option A:optionA,Option B:optionB"
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="SUB_GROUP">Sub Group</label>
              <input
                type="text"
                id="SUB_GROUP"
                name="SUB_GROUP"
                value={formData.SUB_GROUP || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                placeholder="Optional grouping label — settings sharing a value are grouped together on the connector's Config tab (e.g. CC Token Swap)"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="required">Required</label>
              <select
                id="required"
                name="required"
                value={formData.required || 'N'}
                onChange={handleChange}
                disabled={mode === 'view'}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="is_inheritable">Inheritable</label>
              <select
                id="is_inheritable"
                name="is_inheritable"
                value={formData.is_inheritable || 'Y'}
                onChange={handleChange}
                disabled={mode === 'view'}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="must_be_overridden">Must Be Overridden</label>
              <select
                id="must_be_overridden"
                name="must_be_overridden"
                value={formData.must_be_overridden || 'N'}
                onChange={handleChange}
                disabled={mode === 'view'}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="SETTINGVALUE">Setting Value</label>
              <textarea
                id="SETTINGVALUE"
                name="SETTINGVALUE"
                value={formData.SETTINGVALUE || ''}
                onChange={handleChange}
                disabled={mode === 'view'}
                rows="6"
                placeholder="Enter the setting value (supports multi-line text)"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          {mode === 'view' && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/admin/marketplace/${marketplaceid}/setting/${id}/edit`)}
                className="btn-primary"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="btn-danger"
                disabled={loading}
              >
                Delete
              </button>
            </>
          )}
          {(mode === 'create' || mode === 'edit') && (
            <>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (mode === 'create' ? 'Create' : 'Update')}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/marketplace/${marketplaceid}/edit?tab=marketplacesettings`)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              {mode === 'edit' && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="btn-danger"
                  disabled={loading}
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default MarketplaceSettingForm;
